/* RepeatingDisplay — a template rendered once per row of a data source.
 *
 * 0.41.84 — Distinct from the Repeater, which COLLECTS repeated values
 * through one reusable form and shows them back as a label/value
 * summary. This renders the template itself, once per row, read-only.
 *
 * Read-only is what makes it tractable. The Repeater's per-instance
 * naming exists for ONE draft at a time; thirty simultaneous editable
 * instances would mean thirty sets of element ids, thirty validation
 * states and a rules engine that addresses elements by a single id.
 * Displaying thirty rows needs none of that.
 *
 * Rows come from whichever source the element names, resolved by the
 * caller and handed in — the same rows a table would draw, so a grid
 * of cards and a table of the same data cannot disagree.
 */

import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RdSearchContext, RdRowContext, rowIdentity } from '../lib/rdCardContext';
// 0.42.27 - The same pagination a table uses.
import { PaginationContext, usePaginationStateForTable } from './PaginationContext';

/* One card's worth of elements.
 *
 * A child names the column it shows in `bind_column`. Anything without
 * one renders as authored — a heading or a line of guidance that is the
 * same on every card. */
import { fillColumnTokens } from './RepeatingColumns';
import rdStyles from './RepeatingDisplay.module.css';

/* One card's worth of elements, filled from one row.
 *
 * 0.41.87 - Walks every STRING property and fills `{column:…}` tokens.
 * No list of which properties of which types can bind: a token works
 * wherever an admin put it, including a card's fifteenth section value,
 * which the hand-written map of 0.41.86 did not know existed.
 *
 * Children are walked too, so a column group inside a card behaves.
 */
function bindChild(child, row, readValue) {
  if (!child || typeof child !== 'object') return child;
  const out = Array.isArray(child) ? [] : {};
  for (const [key, val] of Object.entries(child)) {
    if (typeof val === 'string') {
      out[key] = fillColumnTokens(val, row, readValue);
      // 0.49.44 - Which texts were filled from the row, so a card line
      // whose value came out empty can tell it had one to show.
      if (val !== out[key] && val.indexOf('{column:') !== -1) {
        out.__bound = { ...(out.__bound || {}), [key]: true };
      }
    } else if (val && typeof val === 'object') {
      out[key] = bindChild(val, row, readValue);
    } else {
      out[key] = val;
    }
  }
  // An image whose src became a path needs the proxy url; the caller
  // knows the connection, so it is flagged rather than built here.
  if (child.type === 'image' && out.src && out.src !== child.src) {
    out._bound_src = true;
  }
  /* 0.45.82 - An information card set to show a picture whose row has
     none: flagged, so the card shows the "no picture" box rather than
     quietly falling back to a plain divider line. */
  if (child.type === 'information_card' && typeof child.image_src === 'string'
      && child.image_src.trim() && !String(out.image_src || '').trim()) {
    out._image_missing = true;
  }
  return out;
}

/* 0.46.46 - Grouping. A display set to Group by a content library's
 * link (a question's group, and optionally its section) shows, per
 * group, the elements marked "Once per group" (a Title with
 * {column:group_title}, an FAQ of the group's questions), then a card
 * per row from the other elements. Groups follow their own table's
 * order (<prefix>_order); rows keep theirs inside a group. Rows with no
 * group come last. */
/* 0.49.22 - An API display groups by any column, parent.code
 * included: read through readValue when the row has no flat key. */
function groupRows(rows, prefix, readValue) {
  const groups = [];
  const byKey = new Map();
  for (const row of rows) {
    let id = row ? row[prefix] : undefined;
    if (id === undefined && row && readValue && prefix.includes('.')) id = readValue(row, prefix);
    const k = id === undefined || id === null || id === '' ? '' : String(id);
    if (!byKey.has(k)) {
      const g = { key: k, order: Number(row && row[`${prefix}_order`]), rows: [] };
      byKey.set(k, g); groups.push(g);
    }
    byKey.get(k).rows.push(row);
  }
  return groups.sort((a, b) => {
    if (a.key === '' || b.key === '') return a.key === '' ? 1 : -1;
    const ao = Number.isFinite(a.order) ? a.order : Infinity;
    const bo = Number.isFinite(b.order) ? b.order : Infinity;
    return ao - bo;
  });
}

/* An element shown once per group, filled from the group's first row.
 * An FAQ reading "this group's rows" gets one question per row.
 * 0.46.56 - Also an FAQ inside a Column group shown once per group. */
function groupFaq(node, rows) {
  if (!node || typeof node !== 'object') return node;
  if (node.type === 'faq' && node.source === 'group_rows') {
    const q = node.question_column || '';
    const a = node.answer_column || '';
    const text = (row, col) => {
      if (!col) return '';
      const md = row[`${col}_md`];
      const v = md !== undefined && md !== null && md !== '' ? md : row[col];
      return v === undefined || v === null ? '' : String(v);
    };
    return { ...node, source: undefined,
             items: rows.map(row => ({ question: text(row, q), answer: text(row, a) }))
               .filter(it => it.question.trim() !== '') };
  }
  if (Array.isArray(node.children)) {
    return { ...node, children: node.children.map(c => groupFaq(c, rows)) };
  }
  return node;
}
function bindGroupChild(child, rows, readValue) {
  return groupFaq(bindChild(child, rows[0] || {}, readValue), rows);
}

/* 0.46.56 - An image filled from a column, at any depth (an Image in a
 * Column group of the card too), gets the address the caller builds. */
function withImageUrls(node, imageUrlFor) {
  if (!node || typeof node !== 'object' || !imageUrlFor) return node;
  let out = node;
  if (node._bound_src) out = { ...out, src: imageUrlFor(node.src) };
  if (Array.isArray(node.children)) {
    out = { ...out, children: node.children.map(c => withImageUrls(c, imageUrlFor)) };
  }
  return out;
}

/* 0.49.43 - Search and filters. Around the cards:
 *   - the elements set to "Shows: Once, above the cards" (the search
 *     field, the filter dropdowns...), drawn once, always, also when no
 *     row passes, so the visitor can change them;
 *   - the results line (the count and Clear filters), from the caller;
 *   - the matches of the search highlighted in the cards, with the CSS
 *     Custom Highlight API: ranges over the text, so React's own DOM is
 *     never touched. Browsers without it show no highlight. */
let _hlSeq = 0;
export default function RepeatingDisplay(props) {
  const { el, renderOnce, resultsLine = null, highlightWords = null, highlightColor = '', narrowed = false } = props;
  const once = (Array.isArray(el && el.children) ? el.children : []).filter(c => c && c.rd_repeat === 'once');
  const cardsRef = useRef(null);
  const nameRef = useRef(null);
  if (!nameRef.current) { _hlSeq += 1; nameRef.current = `gt18rd${_hlSeq}`; }
  const words = Array.isArray(highlightWords) ? highlightWords.filter(Boolean) : [];
  const wordKey = words.join('\u0001');
  useEffect(() => {
    const name = nameRef.current;
    const root = cardsRef.current;
    const api = typeof CSS !== 'undefined' && CSS.highlights && typeof Highlight !== 'undefined';
    if (!api) return undefined;
    if (!root || !words.length) { CSS.highlights.delete(name); return undefined; }
    const paint = () => {
      const ranges = [];
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const lowered = words.map(w => w.toLowerCase());
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        const t = n.nodeValue ? n.nodeValue.toLowerCase() : '';
        if (!t.trim()) continue;
        for (const w of lowered) {
          let at = t.indexOf(w);
          while (at !== -1) {
            const r = new Range();
            r.setStart(n, at); r.setEnd(n, at + w.length);
            ranges.push(r);
            at = t.indexOf(w, at + w.length);
          }
        }
      }
      CSS.highlights.set(name, new Highlight(...ranges));
    };
    paint();
    // Cards drawn a moment later (a library arriving) are painted too.
    const mo = new MutationObserver(paint);
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    return () => { mo.disconnect(); CSS.highlights.delete(name); };
  }, [wordKey]); // eslint-disable-line react-hooks/exhaustive-deps
  /* 0.49.56 - While searching or filtering, the cards keep the room they
     had when it started: fewer cards leave space under them instead of
     making the page shorter, which pulled the page up and made the
     search field slide down the screen while typing. The room grows
     when more cards appear and never shrinks during the search. It is
     given back when the search and the filters are cleared, when every
     card is back. */
  const innerRef = useRef(null);
  const lastHeightRef = useRef(0);
  const [reserve, setReserve] = useState(0);
  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    if (!narrowed) {
      if (reserve) setReserve(0);
      lastHeightRef.current = inner.offsetHeight;
      return;
    }
    // The search just started: the height before it narrowed anything.
    const now = inner.offsetHeight;
    const want = Math.max(reserve || lastHeightRef.current, now);
    if (want !== reserve) setReserve(want);
  });
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const h = inner.offsetHeight;
      if (!narrowed) lastHeightRef.current = h;
      else setReserve(r => (h > r ? h : r));
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [narrowed]);
  const hlCss = colorOrDefault(highlightColor);
  // 0.49.55 - The search words and the cards opened by hand, for the cards.
  const handRef = useRef(new Set());
  const searchCtx = useMemo(() => ({ words: words.map(w => w.toLowerCase()), wordKey, hand: handRef.current }),
    [wordKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {once.length > 0 && typeof renderOnce === 'function' && (
        <div data-align-row="" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
                      marginLeft: 'calc(-1 * var(--container-padding-x))',
                      marginRight: 'calc(-1 * var(--container-padding-x))' }}>
          {once.map((c, k) => renderOnce(c, `once${k}`))}
        </div>
      )}
      {resultsLine}
      {words.length > 0 && <style>{`::highlight(${nameRef.current}) { background-color: ${hlCss}; color: inherit; }`}</style>}
      <div ref={cardsRef} style={reserve ? { minHeight: reserve } : undefined}>
        <div ref={innerRef}>
          <RdSearchContext.Provider value={searchCtx}>
            <RepeatingDisplayCards {...props} />
          </RdSearchContext.Provider>
        </div>
      </div>
    </>
  );
}

function colorOrDefault(c) {
  const s = String(c || '').trim();
  if (!s) return 'var(--color-warning-light, #fff1c2)';
  if (s.startsWith('--')) return `var(${s})`;
  // A color CSS value (colorCss): never anything that could end the rule.
  return /^(#[0-9a-fA-F]{3,8}|var\(--[A-Za-z0-9-]+\)|rgba?\([0-9.,\s%]+\))$/.test(s) ? s : 'var(--color-warning-light, #fff1c2)';
}

function RepeatingDisplayCards({
  el,
  rows: allRows,
  /* 0.42.27 - The element's id, which pagination keys its state by.
     Empty means no pagination, whatever the setting says. */
  paginationKey = '',
  /* 0.42.40 - True while this display's API call is running. */
  loading = false,
  /* 0.45.61 - Pages from the API: `rows` is the page to show, and
     ApiPageDriver reports the count. */
  serverPaged = false,
  readValue,
  renderChild,
  imageUrlFor,
}) {
  /* 0.42.27 - Paginate exactly as a table does.
   *
   * The count is REPORTED rather than read from the structure, because
   * a display's rows come from an API response or a library at run
   * time - the structure cannot know how many there will be. This is
   * the route LibraryBackedTable already takes.
   *
   * Hooks are called unconditionally, as React requires; whether they
   * do anything is decided by what they return. */
  const pctx = useContext(PaginationContext);
  const pstate = usePaginationStateForTable(
    el.pagination_enabled ? paginationKey : '');
  const total = Array.isArray(allRows) ? allRows.length : 0;
  useEffect(() => {
    if (serverPaged) return;
    if (el.pagination_enabled && paginationKey && pctx && pctx.reportRowCount) {
      pctx.reportRowCount(paginationKey, total);
    }
  }, [el.pagination_enabled, paginationKey, pctx, total, serverPaged]);

  let rows = Array.isArray(allRows) ? allRows : [];
  // 0.46.46 - Grouped: no pagination (a group split across pages reads badly).
  const grouped = !!el.group_by;
  if (el.pagination_enabled && pstate && !serverPaged && !grouped) {
    const per = Math.max(1, Number(pstate.perPage) || 10);
    const page = Math.max(1, Number(pstate.currentPage) || 1);
    rows = rows.slice((page - 1) * per, page * per);
  }

  // 0.49.43 - Elements shown once above the cards are not part of a card.
  const allChildren = (Array.isArray(el.children) ? el.children : []).filter(c => !c || c.rd_repeat !== 'once');
  // 0.46.46 - When grouped, elements marked once per group (or per
  // outer group) are not part of a card.
  const template = grouped ? allChildren.filter(c => !c || !c.rd_repeat) : allChildren;

  if (allChildren.length === 0) {
    // Nothing to render per row. Say so rather than showing an empty
    // strip that looks like a data problem.
    return (
      <div style={{
        // 0.44.74 - As the repeater's notice: a light dashed border on
        // bg-alt (--color-bg-100 was never defined).
        border: '1px dashed var(--color-text-400)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-4)',
        background: 'var(--color-bg-alt)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-600)',
        fontStyle: 'italic',
      }}>
        This repeating display has no template yet. Add elements inside
        it in the form editor.
      </div>
    );
  }

  /* 0.42.40 - Nothing at all while the call runs.
   *
   * The Henaket loader specification: never show an empty-state message
   * while the loader should be showing; it appears only once loading has
   * truly finished. A call in progress has no rows YET, so without this
   * the "no rows" wording would sit beside a loader saying the opposite.
   * A loader placed here, following the same connection, fills the
   * space instead. */
  if (loading) return null;

  if (!rows || rows.length === 0) {
    // "No results" and "not asked yet" look identical to a citizen, so
    // the admin supplies the wording.
    return el.empty_text
      ? <p style={{ color: 'var(--color-text-700)' }}>{el.empty_text}</p>
      : null;
  }

  /* 0.46.56 - The page grid places the elements, not the display.
   *
   * The display is the loop: rows from a source, grouping, maximum
   * cards, pagination, the card a visitor clicks. Each row's elements
   * go into one wrapping row, as on a page, at their own column sizes
   * (Size and position): a Guide card at 4 columns gives 3 a line on
   * a wide screen, at 12 on phones 1. A card of several elements is a
   * Column group. Cards across, Card width and Cards never narrower
   * than are gone; the 0.46.56 migration turned them into these
   * sizes.
   *
   * Spacing is each element's own, as on a page: 16px under a card,
   * 32px between cards side by side (the gutter). The last line's
   * 16px is taken back, since the display's own column adds it. */
  // 0.46.59 - `tpl`: the elements repeated per row; the display's own
  // cards by default, or those of a Column group shown once per group.
  // 0.46.60 - `inGroup`: the Column group the cards are drawn in. Their
  // sizes are then page columns, as for any element of a Column group
  // (5 in a group of 8 is 5/8 of it), not twelfths of the group.
  const renderGrid = (list, keyBase = '', last = true, tpl = template, inGroup = null) => {
    if (tpl.length === 0 || list.length === 0) return null;
    /* 0.46.63 - The last line's own space below is taken back: the
       last element's (32px for a guide card, 16px for most). */
    const lastEl = tpl[tpl.length - 1] || {};
    const mb = Number.isFinite(lastEl.margin_bottom) ? lastEl.margin_bottom
      : lastEl.type === 'guide_card' ? 8 : 4;
    return (
      <div className={rdStyles.flow} data-align-row="" style={{
        display: 'flex',
        flexWrap: 'wrap',
        marginLeft: 'calc(-1 * var(--container-padding-x))',
        marginRight: 'calc(-1 * var(--container-padding-x))',
        marginBottom: last && mb > 0 ? `calc(-1 * var(--space-${mb}))` : 0,
      }}>
        {(() => {
          /* 0.49.55 - Each row's cards keyed by the row itself, not by its
             place: when the search or a filter leaves out rows, a card the
             visitor opened stays with its row (it followed the place, so
             another row's card showed open). */
          const seen = new Map();
          return list.map((row) => {
            const i = rows.indexOf(row);
            const base = rowIdentity(row);
            const n = (seen.get(base) || 0) + 1;
            seen.set(base, n);
            const rid = n > 1 ? `${base}#${n}` : base;
            return (
              <RdRowContext.Provider key={`${keyBase}r${rid}`} value={rid}>
                {tpl.map((child, k) => renderChild(
                  withImageUrls(bindChild(child, row, readValue), imageUrlFor),
                  `${keyBase}${rid}:${k}`, i, row, inGroup))}
              </RdRowContext.Provider>
            );
          });
        })()}
      </div>
    );
  };

  if (!grouped) return renderGrid(rows);

  /* 0.46.46 - Grouped. Per outer group (a section): its "once per
     section" elements; then per group: its "once per group" elements
     and its rows' cards. */
  const heads = (level) => allChildren.filter(c => c && c.rd_repeat === level);
  const outerHeads = el.group_outer ? heads('outer') : [];
  const groupHeads = heads('group');
  /* 0.46.48 - The heads carry their own Henaket spacing (a Title's
     space above and below, 16px under an FAQ), so the containers add
     no gap of their own: a group title sits 40px under the previous
     group's last question, as it would outside the display. Flex, as a
     page row is, so a Title's space above adds to the space below the
     element before it instead of collapsing into it. 0.46.55 - And
     wrapping, as a page row: elements whose columns fit sit side by
     side. The last element of the whole display has no space below
     (the display's own column adds it). */
  /* 0.46.57 - A Column group shown once per group can hold the
     group's cards ("Holds this group's cards"): they are drawn inside
     it, after its own elements, instead of under the group's heading.
     So a group's title and its cards can sit beside the group's
     picture. The first such group holds them. */
  const holderOf = (list) => list.find(c => c && c.type === 'column_group' && c.rd_cards);
  /* 0.46.59 - Inside a Column group shown once per group, an element
     set to For every row (`rd_each`) repeats for each row of the group,
     right there: the group's cards sit inside the group, after the
     elements before them. It is left out of the binding to the group's
     first row and drawn per row instead. */
  const eachOf = (child) => (child && child.type === 'column_group' && Array.isArray(child.children)
    ? child.children.filter(c => c && c.rd_each) : []);
  const bindHead = (child, gRows, keyBase) => {
    const each = eachOf(child);
    if (!each.length) return withImageUrls(bindGroupChild(child, gRows, readValue), imageUrlFor);
    const shell = { ...child, children: child.children.map(c => (c && c.rd_each ? { __each: true } : c)) };
    const bound = withImageUrls(bindGroupChild(shell, gRows, readValue), imageUrlFor);
    let placed = false;
    return { ...bound, children: bound.children.flatMap(c => {
      if (!(c && c.__each)) return [c];
      if (placed) return [];
      placed = true;
      return [{ id: `${child.id}__each`, type: 'rd_cards_slot',
                col_span: 12, col_md_span: 12, col_lg_span: 12,
                _node: renderGrid(gRows, `${keyBase}e`, true, each, child) }];
    }) };
  };
  const renderHeads = (list, gRows, keyBase, lastOfAll = false) => {
    const holder = holderOf(list);
    return list.map((child, k) => {
      let bound = bindHead(child, gRows, `${keyBase}h${k}`);
      if (holder && child === holder && template.length > 0) {
        // Added after binding: the slot carries rendered cards, which
        // the token filling must not walk.
        bound = { ...bound, children: [...(bound.children || []), {
          id: `${child.id}__cards`, type: 'rd_cards_slot',
          col_span: 12, col_md_span: 12, col_lg_span: 12,
          _node: renderGrid(gRows, `${keyBase}c`, true),
        }] };
      }
      if (lastOfAll && (k === list.length - 1 || child === holder || eachOf(child).length)) bound = { ...bound, margin_bottom: 0 };
      const i = rows.indexOf(gRows[0]);
      return renderChild(bound, `${keyBase}h${k}`, i, gRows[0]);
    });
  };
  const headRow = (children) => (
    <div data-align-row="" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
                  marginLeft: 'calc(-1 * var(--container-padding-x))',
                  marginRight: 'calc(-1 * var(--container-padding-x))' }}>
      {children}
    </div>
  );
  // 0.46.57 - Cards held by a Column group of the heading are not
  // drawn again under it.
  const cardsBelow = template.length > 0 && !holderOf(groupHeads);
  const renderGroup = (g, keyBase, last) => (
    <div key={keyBase} className={rdStyles.group}>
      {groupHeads.length > 0 && headRow(renderHeads(groupHeads, g.rows, `${keyBase}g`, last && !cardsBelow))}
      {cardsBelow && renderGrid(g.rows, `${keyBase}r`, last)}
    </div>
  );
  const inner = (list, keyBase, lastOuter) => {
    const gs = groupRows(list, el.group_by, readValue);
    return gs.map((g, n) => renderGroup(g, `${keyBase}${n}-`, lastOuter && n === gs.length - 1));
  };
  if (!el.group_outer) return <div>{inner(rows, '', true)}</div>;
  const outers = groupRows(rows, el.group_outer, readValue);
  return (
    <div>
      {outers.map((og, n) => (
        <div key={`o${n}`}>
          {outerHeads.length > 0 && headRow(renderHeads(outerHeads, og.rows, `o${n}h`))}
          {inner(og.rows, `o${n}-`, n === outers.length - 1)}
        </div>
      ))}
    </div>
  );
}
